import React from "react";
import { ChevronRightIcon, HomeIcon } from "@heroicons/react/20/solid";
import { Link, useLocation } from "react-router-dom";

interface BreadcrumbPage {
  name: string;
  href: string;
  current: boolean;
}

interface BreadcrumbProps {
  pages: BreadcrumbPage[];
  showHomeIcon?: boolean;
  className?: string;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  pages,
  showHomeIcon = true,
  className = "",
}) => {
  const location = useLocation();

  return (
    <nav aria-label="Breadcrumb" className={`hidden md:flex ${className}`}>
      <ol className="flex items-center space-x-4">
        {showHomeIcon && (
          <li>
            <div>
              <Link
                to="/"
                className="text-gray-400 hover:text-gray-500 transition-colors duration-200"
              >
                <HomeIcon aria-hidden="true" className="h-5 w-5 shrink-0" />
                <span className="sr-only">Home</span>
              </Link>
            </div>
          </li>
        )}

        {pages.map((page, index) => (
          <li key={page.name}>
            <div className="flex items-center">
              <ChevronRightIcon
                aria-hidden="true"
                className="h-5 w-5 shrink-0 text-gray-400"
              />
              <Link
                to={page.href}
                aria-current={page.current ? "page" : undefined}
                className={`
                  ml-4 text-sm font-medium transition-colors duration-200
                  ${
                    page.current
                      ? "text-gray-700 cursor-default pointer-events-none"
                      : "text-gray-500 hover:text-gray-700"
                  }
                `}
              >
                {page.name}
              </Link>
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
};

// Helper function to generate breadcrumb pages from a route
export const generateBreadcrumbPages = (path: string): BreadcrumbPage[] => {
  const segments = path.split("/").filter(Boolean);

  return segments.map((segment, index) => ({
    name: segment.charAt(0).toUpperCase() + segment.slice(1),
    href: "/" + segments.slice(0, index + 1).join("/"),
    current: index === segments.length - 1,
  }));
};
